<template>
  <div class="rf24-pair">
    <el-alert
      :title="$t('rf24Pair.warningTitle')"
      :description="$t('rf24Pair.warningDesc')"
      type="warning"
      :closable="false"
      show-icon
      class="rf-pair-warning">
    </el-alert>
    <el-alert
      v-if="!serialSupported"
      :title="$t('rf24Pair.serialUnsupported')"
      type="warning"
      :closable="false"
      show-icon>
    </el-alert>

    <div class="rf-toolbar">
      <el-button
        type="primary"
        icon="el-icon-connection"
        :loading="connecting"
        :disabled="connected"
        @click="connect">{{ $t('rf24Pair.connect') }}</el-button>
      <el-button
        type="warning"
        icon="el-icon-switch-button"
        :disabled="!connected"
        @click="disconnect">{{ $t('rf24Pair.disconnect') }}</el-button>
      <span v-if="connected" class="rf-connected"><i class="el-icon-success"></i> {{ $t('rf24Pair.connected') }}</span>
    </div>

    <template v-if="connected">
      <el-descriptions :column="2" border size="small" class="rf-desc">
        <el-descriptions-item :label="$t('rf24Pair.receiverId')">{{ localIdHex || '--' }}</el-descriptions-item>
        <el-descriptions-item :label="$t('rf24Pair.currentPair')">
          <el-tag :type="pairFlag ? 'success' : 'info'" size="mini">{{ pairFlag ? $t('rf24Pair.paired') : $t('rf24Pair.unpaired') }}</el-tag>
          <span v-if="pairFlag" class="rf-addr">{{ addrHex }}</span>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('rf24Pair.runState')">{{ stateText }}</el-descriptions-item>
        <el-descriptions-item :label="$t('rf24Pair.protocolVersion')">{{ protoVer }}</el-descriptions-item>
      </el-descriptions>

      <div class="rf-actions">
        <el-button type="primary" icon="el-icon-search" :loading="scanning" @click="startScan">
          {{ scanning ? $t('rf24Pair.scanning') : $t('rf24Pair.search') }}
        </el-button>
        <el-button icon="el-icon-refresh" :disabled="!scanning" @click="loadResults">{{ $t('rf24Pair.refresh') }}</el-button>
        <el-tag v-if="scanning" type="warning" size="small">{{ $t('rf24Pair.remaining') }} {{ countdown }}s</el-tag>
        <el-button type="text" icon="el-icon-close" :disabled="!scanning" @click="cancelScan">{{ $t('rf24Pair.cancelSearch') }}</el-button>
      </div>

      <el-table
        v-if="devices.length"
        :data="devices"
        size="small"
        highlight-current-row
        @current-change="onRowSelect"
        class="rf-table">
        <el-table-column width="46">
          <template slot-scope="s">
            <el-radio v-model="selectedId" :label="s.row.idHex">&nbsp;</el-radio>
          </template>
        </el-table-column>
        <el-table-column prop="idHex" :label="$t('rf24Pair.deviceId')" min-width="150"></el-table-column>
        <el-table-column prop="modelText" :label="$t('rf24Pair.model')" width="110"></el-table-column>
        <el-table-column :label="$t('rf24Pair.actions')" width="120">
          <template slot-scope="s">
            <el-button size="mini" type="primary" @click="doPair(s.row)">{{ $t('rf24Pair.pair') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-else class="rf-empty">
        <template v-if="scanning">
          {{ $t('rf24Pair.scanHint') }}
          <div class="rf-led-hint"><span class="rf-led red"></span><span class="rf-led green"></span>{{ $t('rf24Pair.ledHint') }}</div>
        </template>
        <template v-else>
          {{ $t('rf24Pair.searchHint') }}
        </template>
      </div>

      <el-steps v-if="pairState" :active="pairActive" finish-status="success" simple class="rf-steps">
        <el-step :title="$t('rf24Pair.step1')"></el-step>
        <el-step :title="$t('rf24Pair.step2')"></el-step>
        <el-step :title="$t('rf24Pair.step3')"></el-step>
      </el-steps>

      <div v-if="pairState===3" class="rf-led-hint success">
        <i class="el-icon-success"></i> {{ $t('rf24Pair.successHint') }}
      </div>

      <div class="rf-actions">
        <el-button type="danger" plain icon="el-icon-delete" :disabled="!pairFlag" @click="unpair">{{ $t('rf24Pair.unpair') }}</el-button>
        <el-button icon="el-icon-refresh" @click="refreshInfo">{{ $t('rf24Pair.refreshState') }}</el-button>
      </div>

      <div v-if="message" :class="['rf-msg', messageType]">{{ message }}</div>
    </template>
  </div>
</template>

<script>
const CMD = {
  GET_INFO: 0x01,
  START_SCAN: 0x02,
  GET_RESULTS: 0x03,
  PAIR: 0x04,
  GET_STATUS: 0x05,
  CANCEL: 0x06,
  UNPAIR: 0x07
};
const PROTO_VER = 0x01;
const STATE = {
  IDLE: 0,
  SCANNING: 1,
  PAIRING: 2,
  SUCCESS: 3,
  FAILED: 4
};
const STATE_KEYS = ['stateIdle', 'stateScanning', 'statePairing', 'stateSuccess', 'stateFailed'];

function crc16Ccitt(bytes, start, endExclusive) {
  let crc = 0xffff;
  for (let i = start; i < endExclusive; i++) {
    crc ^= (bytes[i] << 8) & 0xffff;
    for (let b = 0; b < 8; b++) {
      crc = (crc & 0x8000) ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc & 0xffff;
}

function bytesToHex(arr) {
  return Array.from(arr).map(v => v.toString(16).toUpperCase().padStart(2, '0')).join('');
}

export default {
  name: 'Rf24Pair',
  data() {
    return {
      serialSupported: typeof navigator !== 'undefined' && !!navigator.serial,
      port: null,
      writer: null,
      reader: null,
      readLoopPromise: null,
      connected: false,
      connecting: false,
      rxbuf: new Uint8Array(0),
      pending: {},
      seq: 0,
      localIdHex: '',
      addrHex: '',
      pairFlag: false,
      protoVer: PROTO_VER,
      state: STATE.IDLE,
      progress: 0,
      scanning: false,
      countdown: 0,
      devices: [],
      selectedId: '',
      pairState: 0,
      message: '',
      messageType: 'info',
      pollTimer: null,
      countdownTimer: null
    };
  },
  computed: {
    stateText() {
      return this.$t('rf24Pair.' + (STATE_KEYS[this.state] || 'stateUnknown'));
    },
    pairActive() {
      if (this.pairState === STATE.SUCCESS) return 3;
      if (this.pairState === STATE.PAIRING) return Math.max(1, this.progress);
      if (this.pairState === STATE.FAILED) return 0;
      return 0;
    }
  },
  beforeDestroy() {
    this.stopTimers();
    this.disconnect();
  },
  methods: {
    info(msg, type) {
      this.message = msg;
      this.messageType = type || 'info';
    },
    buildFrame(cmd, seq, payload) {
      const p = payload || [];
      const buf = [0xC5, 0x3A, PROTO_VER, cmd, seq & 0xff, p.length].concat(p);
      const crc = crc16Ccitt(buf, 2, buf.length);
      buf.push(crc & 0xff, (crc >> 8) & 0xff);
      return new Uint8Array(buf);
    },
    request(cmd, payload, timeoutMs) {
      return new Promise((resolve, reject) => {
        const seq = (this.seq = (this.seq + 1) & 0xff);
        const timer = setTimeout(() => {
          if (this.pending[seq]) {
            delete this.pending[seq];
            reject(new Error(this.$t('rf24Pair.timeout')));
          }
        }, timeoutMs || 2000);
        this.pending[seq] = { resolve, reject, timer };
        this.writer.write(this.buildFrame(cmd, seq, payload || []))
          .catch(err => {
            clearTimeout(timer);
            delete this.pending[seq];
            reject(err);
          });
      });
    },
    feed(chunk) {
      const merged = new Uint8Array(this.rxbuf.length + chunk.length);
      merged.set(this.rxbuf, 0);
      merged.set(chunk, this.rxbuf.length);
      let buf = merged;

      while (buf.length >= 9) {
        let i = 0;
        while (i + 1 < buf.length && !(buf[i] === 0x3A && buf[i + 1] === 0xC5)) i++;
        if (i > 0) buf = buf.slice(i);
        if (buf.length < 9) break;
        if (!(buf[0] === 0x3A && buf[1] === 0xC5)) break;

        const cmd = buf[3];
        const seq = buf[4];
        const status = buf[5];
        const len = buf[6];
        const total = 7 + len + 2;
        if (buf.length < total) break;

        const crcCalc = crc16Ccitt(buf, 2, 7 + len);
        const crcRecv = buf[7 + len] | (buf[8 + len] << 8);
        if (crcCalc === crcRecv) {
          const payload = buf.slice(7, 7 + len);
          const p = this.pending[seq];
          if (p) {
            clearTimeout(p.timer);
            delete this.pending[seq];
            p.resolve({ cmd, status, payload });
          }
        }
        buf = buf.slice(total);
      }
      this.rxbuf = buf;
    },
    async connect() {
      if (!this.serialSupported) return;
      this.connecting = true;
      this.rxbuf = new Uint8Array(0);
      try {
        /* 不限定 VID/PID, 列出系统全部串口, 由用户选择接收头对应的 COM 口,
         * 避免接收头 PID 与过滤器不一致时选不到设备 */
        this.port = await navigator.serial.requestPort();
        await this.port.open({ baudRate: 115200 });
        this.writer = this.port.writable.getWriter();
        this.reader = this.port.readable.getReader();
        this.connected = true;
        this.readLoopPromise = this.readLoop();
        await this.refreshInfo();
      } catch (err) {
        this.info(this.$t('rf24Pair.connectFailed') + (err && err.message ? err.message : err), 'error');
      } finally {
        this.connecting = false;
      }
    },
    async readLoop() {
      try {
        while (true) {
          const { value, done } = await this.reader.read();
          if (done) break;
          if (value && value.length) this.feed(value);
        }
      } catch (err) {
        /* 端口关闭时正常结束 */
      }
    },
    async disconnect() {
      this.stopTimers();
      try { if (this.reader) await this.reader.cancel(); } catch (e) {}
      try { if (this.reader) this.reader.releaseLock(); } catch (e) {}
      try { if (this.writer) this.writer.releaseLock(); } catch (e) {}
      try { if (this.port) await this.port.close(); } catch (e) {}
      this.reader = null;
      this.writer = null;
      this.port = null;
      this.connected = false;
      this.scanning = false;
      this.devices = [];
      this.pairState = 0;
      this.rxbuf = new Uint8Array(0);
      this.pending = {};
    },
    applyInfo(payload) {
      if (!payload || payload.length < 15) return;
      this.state = payload[2];
      this.pairFlag = !!payload[3];
      const addr = (payload[4] | (payload[5] << 8) | (payload[6] << 16) | (payload[7] << 24)) >>> 0;
      this.addrHex = '0x' + addr.toString(16).toUpperCase().padStart(8, '0');
      this.localIdHex = bytesToHex(payload.slice(8, 14));
      this.protoVer = payload[14];
    },
    applyStatus(payload) {
      if (!payload || payload.length < 8) return;
      this.state = payload[0];
      this.progress = payload[1];
      this.pairFlag = !!payload[3];
      const addr = (payload[4] | (payload[5] << 8) | (payload[6] << 16) | (payload[7] << 24)) >>> 0;
      this.addrHex = '0x' + addr.toString(16).toUpperCase().padStart(8, '0');
    },
    async refreshInfo() {
      try {
        const r = await this.request(CMD.GET_INFO, []);
        this.applyInfo(r.payload);
      } catch (err) {
        this.info(err.message, 'error');
      }
    },
    async startScan() {
      try {
        const r = await this.request(CMD.START_SCAN, [20]);
        this.state = (r.payload && r.payload[0]) || STATE.SCANNING;
        this.scanning = true;
        this.devices = [];
        this.selectedId = '';
        this.pairState = 0;
        this.countdown = 20;
        this.info(this.$t('rf24Pair.scanStarted'), 'info');
        this.startCountdown(20);
        this.startPolling();
      } catch (err) {
        this.info(this.$t('rf24Pair.scanFailed') + err.message, 'error');
      }
    },
    startCountdown(seconds) {
      if (this.countdownTimer) clearInterval(this.countdownTimer);
      this.countdown = seconds;
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
          this.scanning = false;
        }
      }, 1000);
    },
    startPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.loadResults();
      this.pollTimer = setInterval(() => {
        if (this.pairState === STATE.PAIRING) this.pollPairStatus();
        else this.loadResults();
      }, 1000);
    },
    stopTimers() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
      if (this.countdownTimer) { clearInterval(this.countdownTimer); this.countdownTimer = null; }
    },
    async loadResults() {
      try {
        const r = await this.request(CMD.GET_RESULTS, []);
        const p = r.payload;
        if (!p || p.length < 1) return;
        const count = p[0];
        const list = [];
        let o = 1;
        for (let i = 0; i < count && o + 10 <= p.length; i++, o += 10) {
          const id = p.slice(o, o + 6);
          const model = p[o + 6] | (p[o + 7] << 8);
          list.push({
            idHex: bytesToHex(id),
            idBytes: Array.from(id),
            model,
            modelText: '0x' + model.toString(16).toUpperCase().padStart(4, '0')
          });
        }
        this.devices = list;
        if (count > 0) this.scanning = true;
      } catch (err) {
        /* 轮询失败忽略 */
      }
    },
    onRowSelect(row) {
      if (row) this.selectedId = row.idHex;
    },
    async doPair(row) {
      if (!row) return;
      this.pendingPairId = row.idHex;
      try {
        const r = await this.request(CMD.PAIR, row.idBytes);
        if (r.status !== 0) {
          this.info(this.$t('rf24Pair.pairRequestFailed'), 'error');
          return;
        }
        this.pairState = STATE.PAIRING;
        this.progress = 1;
        this.info(this.$t('rf24Pair.selected') + row.idHex + this.$t('rf24Pair.selectedSuffix'), 'info');
        this.scanning = false;
        if (this.countdownTimer) { clearInterval(this.countdownTimer); this.countdownTimer = null; }
      } catch (err) {
        this.info(this.$t('rf24Pair.pairFailed') + err.message, 'error');
      }
    },
    async pollPairStatus() {
      try {
        const r = await this.request(CMD.GET_STATUS, []);
        this.applyStatus(r.payload);
        this.pairState = this.state;
        if (this.state === STATE.SUCCESS) {
          this.progress = 3;
          this.stopTimers();
          this.info(this.$t('rf24Pair.pairSuccess'), 'success');
          await this.refreshInfo();
        } else if (this.state === STATE.FAILED) {
          this.stopTimers();
          this.info(this.$t('rf24Pair.pairRestore'), 'error');
        }
      } catch (err) {
        /* 忽略单次超时 */
      }
    },
    async cancelScan() {
      try {
        await this.request(CMD.CANCEL, []);
      } catch (err) { /* ignore */ }
      this.scanning = false;
      this.pairState = 0;
      this.stopTimers();
      this.info(this.$t('rf24Pair.cancelled'), 'info');
      await this.refreshInfo();
    },
    async unpair() {
      try {
        await this.$confirm(
          this.$t('rf24Pair.unpairWarning'),
          this.$t('rf24Pair.unpairTitle'),
          {
            type: 'warning',
            confirmButtonText: this.$t('rf24Pair.unpairConfirm'),
            cancelButtonText: this.$t('rf24Pair.unpairCancel'),
            confirmButtonClass: 'el-button--danger',
            closeOnClickModal: false,
            closeOnPressEscape: false
          }
        );
      } catch (e) {
        this.info(this.$t('rf24Pair.unpairCancelled'), 'info');
        return;
      }

      try {
        await this.request(CMD.UNPAIR, []);
        this.pairFlag = false;
        this.info(this.$t('rf24Pair.unpairDone'), 'info');
        await this.refreshInfo();
      } catch (err) {
        this.info(this.$t('rf24Pair.unpairFailed') + err.message, 'error');
      }
    }
  }
};
</script>

<style scoped>
.rf24-pair { padding: 4px 2px; }
.rf-pair-warning { margin-bottom: 12px; }
.rf-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.rf-connected { color: #67C23A; font-size: 14px; }
.rf-desc { margin-bottom: 16px; }
.rf-addr { margin-left: 8px; color: #909399; font-size: 12px; font-family: monospace; }
.rf-actions { display: flex; align-items: center; gap: 10px; margin: 14px 0; }
.rf-empty { color: #909399; font-size: 14px; padding: 22px; text-align: center; background: #fafafa; border-radius: 8px; }
.rf-led-hint { margin-top: 8px; color: #909399; font-size: 13px; line-height: 1.6; }
.rf-led-hint.success { margin-top: 10px; padding: 9px 12px; background: #f0f9eb; color: #529b2e; border-radius: 8px; font-size: 14px; }
.rf-led { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin: 0 3px; vertical-align: middle; }
.rf-led.red { background: #F56C6C; }
.rf-led.green { background: #67C23A; }
.rf-table { margin-bottom: 8px; }
.rf-steps { margin-top: 8px; }
.rf-msg { margin-top: 12px; padding: 10px 14px; border-radius: 8px; font-size: 14px; }
.rf-msg.info { background: #ecf5ff; color: #409EFF; }
.rf-msg.success { background: #f0f9eb; color: #67C23A; }
.rf-msg.error { background: #fef0f0; color: #F56C6C; }
</style>




// WEBPACK FOOTER //
// src/components/Rf24Pair.vue